// Battery Images
import anchorCharging from '../../assets/img/anchor-charging.png'
import anchorEmpty from '../../assets/img/anchor-empty.png'
import anchorFull from '../../assets/img/anchor-full.png'
import anchorHigh from '../../assets/img/anchor-high.png'
import anchorLow from '../../assets/img/anchor-low.png'
import anchorMid from '../../assets/img/anchor-mid.png'
import anchorImg from '../../assets/img/anchor.png'

import batteryEmpty from '../../assets/img/tag_empty_batt.svg'
import batteryFull from '../../assets/img/tag_full_batt.svg'
import batteryHigh from '../../assets/img/tag_high_batt.svg'
import batteryCharging from '../../assets/img/tag_in_charge.svg'
import batteryLow from '../../assets/img/tag_low_batt.svg'
import batteryMid from '../../assets/img/tag_mid_batt.svg'

import { addItemToGroup, getEmptyGroup } from '../../utils/groups'
import { capitalizeFirstLetter, distance3D } from '../../utils/helpers'
import { Anchors } from '../pitches/types'
import { EMPTY_FORMATTED_DEVICE } from './constants'
import { DeviceType, deviceTypes } from './data_types'
import {
  ChargingStates,
  DeviceState,
  FormattedDevice,
  FormattedDeviceMetric,
  FormattedHardware,
  FormattedHardwareStatus,
  HardwareDiagnosticsPacket,
  HardwareStates,
  HardwareStatusTypeKeys,
  StatusType
} from './types'

const debug = false

export function getDeviceTypeKeyFromHardwareStatusKey(
  type: HardwareStatusTypeKeys
): 'anchor' | 'ball' | 'playerTag' {
  if (type === 'tags') return 'playerTag'
  if (type === 'balls') return 'ball'
  return 'anchor'
}

const generateMatrixMetrics = (
  deviceState: DeviceState,
  deviceIndex: number,
  masterId: string,
  hardwareIndex: string[],
  matrix: number[][],
  thresholds: {
    lower: number
    upper: number
  },
  unit: string,
  label: string,
  isRssi = false,
  hardwareState: { [id: string]: DeviceState } = {}
) => {
  const map: {
    [id: string]: FormattedDeviceMetric<number>
  } = {}

  matrix[deviceIndex].forEach((value, j) => {
    const otherDeviceId = hardwareIndex[j]
    let statusType: StatusType
    if (value < thresholds.lower) statusType = 'bad'
    else if (value > thresholds.upper) statusType = 'good'
    else statusType = 'average'

    // Hacky RSSI check - use master RSSI if not a valid reading //
    if (
      isRssi &&
      (otherDeviceId === masterId || deviceState.id === masterId) &&
      value === -105
    ) {
      const otherDeviceState = hardwareState[otherDeviceId]
      value = otherDeviceState?.masterRSSI || value
    }

    map[otherDeviceId] = {
      label,
      value: value,
      statusType,
      display: `${value} ${unit}`
    }
  })

  return map
}

const previousDistancesToMaster: { [id: string]: number } = {}
const lastFluctuations: { [id: string]: number } = {}

// Location arrives on roughly every other tick — the empty ticks carry a null
// location, not a real "out of pitch" reading, while the device stays online.
// Hold the last known location per device so inPitch/ballInPlay don't flicker to
// false between readings. The hold is only valid while online; once the device
// drops offline (or asleep) the held reading is cleared so a stale location is
// never carried past a real disconnect.
type DeviceLocation = NonNullable<DeviceState['dataMetrics']['location']>
const previousLocations: { [id: string]: DeviceLocation } = {}

export function formatDevice(
  deviceId: string,
  diagnostics: HardwareDiagnosticsPacket,
  anchorConfig: Anchors,
  masterId: string,
  stateType: HardwareStates,
  deviceType: DeviceType
): FormattedDevice {
  let stateStatus: StatusType = 'none'

  if (stateType === 'online') stateStatus = 'good'
  else if (stateType === 'offline') stateStatus = 'bad'
  else if (stateType === 'sleep') stateStatus = 'average'

  const deviceState = diagnostics.hardwareState[deviceId]

  if (!deviceState) {
    if (debug)
      console.warn(
        `Device: ${deviceId} not found in diagnostics hardwareState map`
      )
    return {
      ...EMPTY_FORMATTED_DEVICE,
      id: deviceId,
      type: deviceType,
      metrics: {
        ...EMPTY_FORMATTED_DEVICE.metrics,
        status: {
          label: 'Status',
          value: stateType,
          display: capitalizeFirstLetter(stateType),
          statusType: stateStatus
        }
      }
    }
  }

  const index = diagnostics.hardwareIndex.indexOf(deviceId)

  // Generate device maps from hardware data matrices //
  const masterSignalStrength = diagnostics.masterSignalStrength[index]
  const {
    rssiMatrix,
    imuLenMatrix,
    countMatrix,
    hardwareIndex,
    distanceMatrix
  } = diagnostics

  // Anchor RSSI //
  const rssiMatrixMap = generateMatrixMetrics(
    deviceState,
    index,
    masterId,
    hardwareIndex,
    rssiMatrix,
    { lower: -80, upper: -40 },
    'dB',
    'RSSI',
    true,
    diagnostics.hardwareState
  )

  // Anchor Count //
  const countMatrixMap = generateMatrixMetrics(
    deviceState,
    index,
    masterId,
    hardwareIndex,
    countMatrix,
    { lower: 11, upper: 15 },
    '',
    'Count'
  )

  // Anchor Distances //
  const distanceMatrixMap = generateMatrixMetrics(
    deviceState,
    index,
    masterId,
    hardwareIndex,
    distanceMatrix,
    {
      lower: 0,
      upper: 0
    },
    '',
    'Distance'
  )
  // ======== //

  // Get Anchor Position Health and Master info //
  const masterIndex = diagnostics.hardwareIndex.indexOf(masterId)
  const masterInPitchSetup =
    anchorConfig && masterId in anchorConfig ? anchorConfig[masterId] : null
  const anchorInPitchSetup =
    anchorConfig && deviceId in anchorConfig ? anchorConfig[deviceId] : null
  const uwbDistance = diagnostics.distanceMatrix[index]?.[masterIndex]

  let positionHealth = 0
  let positionHealthStatusType: StatusType = 'none'

  if (anchorInPitchSetup && masterInPitchSetup) {
    // distance3D needs a fully-populated 3D point; Coordinate.z is optional on
    // the anchor pos, so assert the concrete shape here.
    // TODO: revisit cast — narrow once anchor pos carries a required z.
    const distanceToMaster = distance3D(
      anchorInPitchSetup.pos as { x: number; y: number; z: number },
      masterInPitchSetup.pos as { x: number; y: number; z: number }
    )
    positionHealth = Math.abs(distanceToMaster - uwbDistance)

    if (positionHealth <= 0.3) {
      positionHealthStatusType = 'good'
    } else if (positionHealth <= 1) {
      positionHealthStatusType = 'average'
    } else if (positionHealth > 1) {
      positionHealthStatusType = 'bad'
    }
  }
  // ======== //

  const { dataMetrics } = deviceState

  // Carry the last known location forward on empty ticks (see previousLocations),
  // but only while the device is online. A non-online device clears its held
  // location so a stale reading is never carried past a real disconnect.
  const isOnline = stateType === 'online'
  if (!isOnline) {
    delete previousLocations[deviceId]
  }
  const location = dataMetrics.location ?? previousLocations[deviceId] ?? null
  if (dataMetrics.location && isOnline) {
    previousLocations[deviceId] = dataMetrics.location
  }

  const rangingQuality = Number(dataMetrics?.ranging?.quality)
  const imuQuality = Number(dataMetrics?.imu?.quality)
  const networkQuality = Number(dataMetrics?.network?.quality)

  let rangingQualityStatus: StatusType = 'none'

  if (rangingQuality >= 0 && rangingQuality <= 6) {
    rangingQualityStatus = 'bad'
  } else if (rangingQuality >= 10) {
    rangingQualityStatus = 'good'
  } else if (rangingQuality > 6 && rangingQuality < 10) {
    rangingQualityStatus = 'average'
  }

  let imuQualityStatus: StatusType = 'none'

  if (imuQuality >= 0 && imuQuality <= 6) {
    imuQualityStatus = 'bad'
  } else if (imuQuality >= 10) {
    imuQualityStatus = 'good'
  } else if (imuQuality > 6 && imuQuality < 10) {
    imuQualityStatus = 'average'
  }

  let networkQualityStatus: StatusType = 'none'

  if (networkQuality >= 0 && networkQuality <= 6) {
    networkQualityStatus = 'bad'
  } else if (networkQuality >= 10) {
    networkQualityStatus = 'good'
  } else if (networkQuality > 6 && networkQuality < 10) {
    networkQualityStatus = 'average'
  }

  // Check for anchor fluctuations //
  let hasFluctuated = false

  if (deviceState.type === deviceTypes.items.anchor.value) {
    const previousDistanceToMaster = previousDistancesToMaster[deviceId]
    const lastFluctuation = lastFluctuations[deviceId]
    const threshold = 1

    if (
      previousDistanceToMaster &&
      uwbDistance &&
      (previousDistanceToMaster - uwbDistance > threshold ||
        previousDistanceToMaster - uwbDistance < -threshold)
    ) {
      hasFluctuated = true
      lastFluctuations[deviceId] = new Date().getTime()
    }

    if (lastFluctuation && new Date().getTime() - lastFluctuation < 10000) {
      hasFluctuated = true
    }

    previousDistancesToMaster[deviceId] = uwbDistance
  }

  return {
    id: deviceId,
    productName: deviceState.productName,
    chargingState: isDeviceCharging(deviceState),
    type: deviceTypes.getTypeByValue(deviceState.type),
    metrics: {
      isInPlay: {
        label: 'BiP',
        display: location?.ballInPlay ? 'In Play' : 'Out of Play',
        value: location?.ballInPlay ?? false,
        statusType: location?.ballInPlay ? 'good' : 'none'
      },
      inPitch: {
        label: 'In Pitch',
        display: location?.inPitch ? 'In Pitch' : 'Out of Pitch',
        value: location?.inPitch ?? false,
        statusType: location?.inPitch ? 'highlight' : 'none'
      },
      battery: {
        label: 'Battery',
        value: deviceState.slowSensors.battery,
        statusType: 'good',
        display: `${deviceState.slowSensors.battery || '-'}%`
      },
      status: {
        label: 'Status',
        value: stateType,
        statusType: stateStatus,
        display: capitalizeFirstLetter(stateType)
      },
      temperature: {
        label: 'Temp',
        value: deviceState.slowSensors?.mcuTemp || null,
        statusType: deviceState.slowSensors?.mcuTemp
          ? deviceState.slowSensors?.mcuTemp > 55
            ? 'bad'
            : 'none'
          : 'none',
        display: deviceState.slowSensors?.mcuTemp
          ? `${deviceState.slowSensors?.mcuTemp}°C`
          : '-'
      },
      positionDataRate: {
        label: 'Pos',
        value: deviceState.positionDataRate,
        statusType: 'good',
        display: `${deviceState.positionDataRate}Hz`
      },
      accelerationStdDev: {
        label: 'Acceleration Std Dev',
        value: deviceState.accStdDeviation,
        statusType: 'good',
        display: `${deviceState.accStdDeviation}`
      },
      positionHealth: {
        label: 'Position Health',
        value: positionHealth,
        statusType: positionHealthStatusType,
        display: positionHealth ? `${positionHealth.toFixed(2)} m` : '-'
      },
      rangingQuality: {
        label: 'RngQ',
        value: rangingQuality,
        statusType: rangingQualityStatus,
        display: dataMetrics?.ranging?.quality || '-'
      },
      imuQuality: {
        label: 'IMUQ',
        value: imuQuality,
        statusType: imuQualityStatus,
        display: dataMetrics?.imu?.quality || '-'
      },
      networkQuality: {
        label: 'NetQ',
        value: networkQuality,
        statusType: networkQualityStatus,
        display: dataMetrics?.network?.quality || '-'
      },
      imuLength: {
        label: 'IMU',
        value: imuLenMatrix[index][index],
        statusType: 'good',
        display: `${imuLenMatrix[index][index]} Hz`
      }
    },

    // Matrices - Device to device readings //
    rssi: rssiMatrixMap,
    count: countMatrixMap,
    distance: distanceMatrixMap,

    // Device Info //
    slowSensor: deviceState.slowSensors,
    master: deviceId === masterId,
    greyListed: deviceState.greyListed,
    position: anchorInPitchSetup?.pos ?? { x: 0, y: 0, z: 0 },
    masterSignalStrength,
    state: deviceState.state,
    seatState: deviceState.seatState,
    lastOnline: deviceState.lastOnline,
    inSession: deviceState.inSession,
    distanceToMaster: uwbDistance,
    hasFluctuated,
    appFwVersion: deviceState.appFwVersion,
    uwbFwVersion: deviceState.uwbFwVersion
  }
}

export function createEmptyFormattedHardwareStatus(): FormattedHardwareStatus {
  return {
    online: {
      name: 'Online',
      devices: getEmptyGroup<FormattedDevice, string>()
    },
    offline: {
      name: 'Offline',
      devices: getEmptyGroup<FormattedDevice, string>()
    },
    sleep: {
      name: 'Sleep',
      devices: getEmptyGroup<FormattedDevice, string>()
    }
  }
}

export function createEmptyFormattedHardware(): FormattedHardware {
  return {
    types: {
      anchor: {
        status: createEmptyFormattedHardwareStatus(),
        devices: getEmptyGroup<FormattedDevice, string>(),
        type: deviceTypes.items.anchor
      },
      playerTag: {
        status: createEmptyFormattedHardwareStatus(),
        devices: getEmptyGroup<FormattedDevice, string>(),
        type: deviceTypes.items.playerTag
      },
      ball: {
        status: createEmptyFormattedHardwareStatus(),
        devices: getEmptyGroup<FormattedDevice, string>(),
        type: deviceTypes.items.ball
      }
    },
    status: createEmptyFormattedHardwareStatus(),
    devices: getEmptyGroup<FormattedDevice, string>()
  }
}

export function formatHardwareData(
  diagnostics: HardwareDiagnosticsPacket,
  anchorConfig: Anchors
) {
  const formattedHardwareState = createEmptyFormattedHardware()

  let masterId: string
  // find master and set id, index and inPitchSetup
  for (const hardwareId in diagnostics.hardwareState) {
    const dev = diagnostics.hardwareState[hardwareId]
    if (dev && dev.slowSensors.uwbRole === 'uwbMain') {
      masterId = dev.id
    }
  }

  const hardwareStatusKeys = Object.keys(diagnostics.status) as HardwareStates[]

  // Generate formatted hardware state //
  hardwareStatusKeys.forEach((hardwareStatusKey: HardwareStates) => {
    if (diagnostics.status[hardwareStatusKey])
      for (const type in diagnostics.status[hardwareStatusKey]) {
        const deviceTypeKey = getDeviceTypeKeyFromHardwareStatusKey(
          type as HardwareStatusTypeKeys
        )
        const deviceType = deviceTypes.getTypeByValue(deviceTypeKey)
        const deviceTypeList =
          diagnostics.status[hardwareStatusKey][type as HardwareStatusTypeKeys]
        deviceTypeList.forEach((deviceId) => {
          const formattedDevice = formatDevice(
            deviceId,
            diagnostics,
            anchorConfig,
            masterId,
            hardwareStatusKey,
            deviceType
          )
          if (formattedDevice.type.key === deviceTypeKey) {
            addItemToGroup(
              formattedHardwareState.types[deviceTypeKey].status[
                hardwareStatusKey
              ].devices,
              formattedDevice,
              deviceId,
              'id',
              deviceId
            )
          }
          addItemToGroup(
            formattedHardwareState.types[deviceTypeKey].devices,
            formattedDevice,
            deviceId,
            'id',
            deviceId
          )
          addItemToGroup(
            formattedHardwareState.status[hardwareStatusKey].devices,
            formattedDevice,
            deviceId,
            'id',
            deviceId
          )
          addItemToGroup(
            formattedHardwareState.devices,
            formattedDevice,
            deviceId,
            'id',
            deviceId
          )
        })
      }
  })

  return formattedHardwareState
}

export const batteryLevels = {
  empty: {
    min: 5,
    batteryImg: batteryEmpty,
    anchorImg: anchorEmpty
  },
  low: {
    min: 25,
    batteryImg: batteryLow,
    anchorImg: anchorLow
  },
  mid: {
    min: 50,
    batteryImg: batteryMid,
    anchorImg: anchorMid
  },
  high: {
    min: 75,
    batteryImg: batteryHigh,
    anchorImg: anchorHigh
  },
  full: {
    min: 100,
    batteryImg: batteryFull,
    anchorImg: anchorFull
  },
  charging: {
    batteryImg: batteryCharging,
    anchorImg: anchorCharging
  },
  noBattery: {
    batteryImg: null,
    anchorImg: anchorImg
  }
}

export function getBatteryLevel(percent: number) {
  if (percent <= batteryLevels.empty.min) {
    return batteryLevels.empty
  } else if (percent <= batteryLevels.low.min) {
    return batteryLevels.low
  } else if (percent <= batteryLevels.mid.min) {
    return batteryLevels.mid
  } else if (percent <= batteryLevels.high.min) {
    return batteryLevels.high
  } else if (percent <= batteryLevels.full.min) {
    return batteryLevels.full
  } else if (percent > batteryLevels.full.min) {
    return batteryLevels.charging
  } else {
    return batteryLevels.noBattery
  }
}

export function isDeviceCharging(device: DeviceState): ChargingStates {
  if (!device) return 'notCharging'
  switch (device.productName) {
    case 'Junco':
    case 'Finch':
      if (device.slowSensors.avgCurrent >= 50) return 'charging'

      if (
        device.slowSensors.battery === 100 &&
        device.slowSensors.avgCurrent <= 0
      )
        return 'fullCharge'

      if (
        device.slowSensors.avgCurrent < 50 &&
        device.slowSensors.avgCurrent > 0
      )
        return 'trickleCharging'
      return 'notCharging'
    case 'Swift':
      if (
        device.slowSensors.battery === 100 &&
        device.slowSensors.avgCurrent <= 0
      )
        return 'fullCharge'

      if (device.slowSensors.avgCurrent > 0) return 'charging'
      return 'notCharging'
    default:
      if (
        device.slowSensors.battery === 100 &&
        device.slowSensors.avgCurrent <= 0
      )
        return 'fullCharge'

      if (device.slowSensors.avgCurrent > 0) return 'charging'
      return 'notCharging'
  }
}
